import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../auth/AuthContext';
import api from '../api/axios';
import '../styles/HomePage.css';
import {getUserWatchingAnime} from '../services/userAnimeService';
import {useClickable} from '../providers/ClickableProvider';
import {Star} from 'lucide-react';

interface Anime {
    id: number;
    title: string;
    imageUrl: string;
    episodes: number;
    rating: number;
    genres: string[];
    studios: string[];
    description?: string;
    episodesWatched?: number;
    releaseDate?: string;
    releaseYear?: number;
    season?: string;
    status: string;
    animeStatus?: string;
    ageRating: string;
    sourceMaterial: string;
}

interface UserAnime {
    id: number;
    animeId: number;
    animeTitle: string;
    animeImageUrl: string;
    status: string;
    rating: number;
    episodesWatched: number;
    isFavorite: boolean;
}

const HomePage: React.FC = () => {
    const {isAuthenticated} = useAuth();
    const [featuredAnime, setFeaturedAnime] = useState<Anime[]>([]);
    const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [watchingAnime, setWatchingAnime] = useState<UserAnime[]>([]);
    const [watchingLoading, setWatchingLoading] = useState(false);
    const navigate = useNavigate();

    // Сезонные состояния - теперь храним только текущий сезон
    const [currentSeasonAnime, setCurrentSeasonAnime] = useState<Anime[]>([]);
    const [currentSeason, setCurrentSeason] = useState<string>('');
    const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
    const [randomAnime, setRandomAnime] = useState<Anime[]>([]);
    const [randomLoading, setRandomLoading] = useState(false);
    const [randomScrollPosition, setRandomScrollPosition] = useState(0);
    const randomContainerRef = useRef<HTMLDivElement>(null);
    const {makeClickable} = useClickable();

    const CARD_WIDTH = 170; // ширина карточки
    const CARD_GAP = 20; // отступ между карточками
    const VISIBLE_CARDS = 1; // сколько карточек показывать за раз

    // Функции для прокрутки
    const handleRandomPrev = () => {
        if (randomContainerRef.current) {
            const container = randomContainerRef.current;
            const cardWidth = CARD_WIDTH + CARD_GAP;
            const targetScroll = Math.max(0, container.scrollLeft - cardWidth);

            container.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }
    };

    const handleRandomNext = () => {
        if (randomContainerRef.current) {
            const container = randomContainerRef.current;
            const cardWidth = CARD_WIDTH + CARD_GAP;
            const maxScroll = container.scrollWidth - container.clientWidth;
            const targetScroll = Math.min(maxScroll, container.scrollLeft + cardWidth);

            container.scrollTo({
                left: targetScroll,
                behavior: 'smooth'
            });
        }
    };

    const handleRandomScroll = () => {
        if (randomContainerRef.current) {
            const container = randomContainerRef.current;
            const newPosition = container.scrollLeft;
            const maxScroll = container.scrollWidth - container.clientWidth;

            setRandomScrollPosition(newPosition);
        }
    };

    const isAtStart = () => {
        if (!randomContainerRef.current) return true;
        const container = randomContainerRef.current;
        // Используем небольшой запас для учета погрешности
        return container.scrollLeft < 10;
    };

    const isAtEnd = () => {
        if (!randomContainerRef.current) return false;
        const container = randomContainerRef.current;
        const maxScroll = container.scrollWidth - container.clientWidth;
        // Используем небольшой запас
        return container.scrollLeft > maxScroll - 10;
    };

    const handleViewAllAnime = () => {
        navigate('/anime');
    };

    const fetchRandomAnime = async () => {
        setRandomLoading(true);
        try {
            const response = await api.get('/anime/random?limit=10');
            setRandomAnime(response.data);
        } catch (error) {
            console.error('Error fetching random anime:', error);
            setRandomAnime([]);
        } finally {
            setRandomLoading(false);
        }
    };

    // Функция для определения текущего сезона по месяцу
    const getCurrentSeason = (): string => {
        const month = new Date().getMonth() + 1; // 1-12

        if (month >= 1 && month <= 3) return 'WINTER';
        if (month >= 4 && month <= 6) return 'SPRING';
        if (month >= 7 && month <= 9) return 'SUMMER';
        return 'FALL';
    };

    // Загрузка данных текущего сезона
    const fetchCurrentSeasonAnime = async () => {
        try {
            const season = getCurrentSeason();
            const year = new Date().getFullYear();

            setCurrentSeason(season);
            setCurrentYear(year);

            // Получаем топ-10 аниме для текущего сезона
            const response = await api.get(`/anime/season/${season}/${year}/top`);
            console.log('Season anime data:', response.data);
            setCurrentSeasonAnime(response.data);
        } catch (error) {
            console.error('Error fetching current season anime:', error);
            setCurrentSeasonAnime([]);
        }
    };

    // Загрузка всех данных
    const fetchAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchCurrentSeasonAnime(),
                fetchRandomAnime()
            ]);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const topThreeSeasonAnime = currentSeasonAnime.slice(0, 3);

    // Автоперелистывание featured аниме
    useEffect(() => {
        // Используем topThreeSeasonAnime вместо featuredAnime
        if (topThreeSeasonAnime.length > 1) {
            const interval = setInterval(() => {
                setCurrentFeaturedIndex((prev) =>
                    prev === topThreeSeasonAnime.length - 1 ? 0 : prev + 1
                );
            }, 10000);

            return () => clearInterval(interval);
        }
    }, [topThreeSeasonAnime.length]);

    useEffect(() => {
        fetchAllData();
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchWatchingAnime();
        }
    }, [isAuthenticated]);

    const handleAnimeClick = (animeId: number) => {
        navigate(`/anime/${animeId}`);
    };

    const fetchWatchingAnime = async () => {
        setWatchingLoading(true);
        try {
            const watchingData = await getUserWatchingAnime();
            setWatchingAnime(watchingData);
        } catch (error) {
            console.error('Error fetching watching anime:', error);
        } finally {
            setWatchingLoading(false);
        }
    };

    const getStatusDisplayName = (status: string): string => {
        const displayNames: { [key: string]: string } = {
            'WATCHING': 'Смотрю',
            'COMPLETED': 'Просмотрено',
            'PLANNED': 'Запланировано',
            'DROPPED': 'Брошено',
            'REWATCHING': 'Пересматриваю'
        };
        return displayNames[status] || status;
    };

    const getSeasonDisplayName = (season: string): string => {
        const seasons: Record<string, string> = {
            'WINTER': 'Зима',
            'SPRING': 'Весна',
            'SUMMER': 'Лето',
            'FALL': 'Осень'
        };
        return seasons[season] || season;
    };

    // Функция для получения отображаемого названия текущего сезона
    const getCurrentSeasonDisplayName = (): string => {
        const seasonName = getSeasonDisplayName(currentSeason);
        return `Сезон ${seasonName.toLowerCase()} ${currentYear}`;
    };

    const handleSeasonClick = (season: string, year: number) => {
        // Создаем параметры для фильтров
        const searchParams = new URLSearchParams();
        searchParams.set('season', season);
        searchParams.set('releaseYear', year.toString());

        // Переходим на страницу аниме с параметрами фильтров
        navigate(`/anime?${searchParams.toString()}`);
    };

    // Функция форматирования даты (такая же как в AnimePage)
    const formatDate = (dateString: string | null): string => {
        if (!dateString) return 'Не указана';
        try {
            const date = new Date(dateString);
            const formatter = new Intl.DateTimeFormat('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
            return formatter.format(date);
        } catch (error) {
            return 'Неверная дата';
        }
    };

    const formatAgeRating = (rating: string): string => {
        if (!rating) return '';

        // Создаем карту для преобразования
        const ratingMap: { [key: string]: string } = {
            'PG_13': 'PG-13',
            'R_17': 'R-17',
            'R_PLUS': 'R+'
        };

        // Если есть в карте - возвращаем преобразованное значение
        if (ratingMap[rating]) {
            return ratingMap[rating];
        }

        // Для G и PG возвращаем как есть
        return rating;
    };

    const currentAnime = featuredAnime[currentFeaturedIndex];

    if (loading) {
        return (
            <div className="anime-container">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    return (
        <div className="home-content-wrapper">
            <div className="anime-container">
                <div className="main-content">
                    {/* Featured Section с каруселью */}
                    <div className="featured-section">
                        <div className="featured-carousel">
                            {topThreeSeasonAnime.map((anime, index) => (
                                <div
                                    key={anime.id}
                                    className={`featured-slide ${index === currentFeaturedIndex ? 'active' : ''}`}
                                    {...makeClickable(`/anime/${anime.id}`)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                            handleAnimeClick(anime.id);
                                        }
                                    }}
                                    style={{
                                        transform: `translateX(${(index - currentFeaturedIndex) * 100}%)`,
                                        position: index === currentFeaturedIndex ? 'relative' : 'absolute',
                                        top: 0,
                                        left: 0,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {/* Основной контент сверху */}
                                    <div className="featured-anime-mini">
                                        {/* Левая часть - картинка */}
                                        <div className="featured-mini-left">
                                            <img
                                                src={anime.imageUrl}
                                                alt={anime.title}
                                                className="featured-mini-poster"
                                                onError={(e) => {
                                                    e.currentTarget.src = "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=250&h=350&fit=crop";
                                                }}
                                            />
                                        </div>

                                        {/* Правая часть - информация */}
                                        <div className="featured-mini-right">
                                            <h2 className="featured-mini-title">{anime.title}</h2>

                                            <div className="featured-mini-meta">
                                                <div className="home-page-meta-column home-page-labels-column">
                                                    <div className="home-page-meta-item">
                                                        <span className="home-page-meta-label">Эпизоды:</span>
                                                    </div>
                                                    <div className="home-page-meta-item">
                                                        <span className="home-page-meta-label">Жанры:</span>
                                                    </div>
                                                    <div className="home-page-meta-item">
                                                        <span className="home-page-meta-label">Статус:</span>
                                                    </div>
                                                    {anime.sourceMaterial && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-label">Первоисточник:</span>
                                                        </div>
                                                    )}
                                                    {anime.releaseDate && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-label">Дата выхода:</span>
                                                        </div>
                                                    )}
                                                    {anime.season && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-label">Сезон:</span>
                                                        </div>
                                                    )}
                                                    {anime.ageRating && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-label">Рейтинг:</span>
                                                        </div>
                                                    )}
                                                    {anime.studios && anime.studios.length > 0 && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-label">Студия:</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="home-page-meta-column home-page-values-column">
                                                    <div className="home-page-meta-item">
                                                        <span className="home-page-meta-value">{anime.episodes}</span>
                                                    </div>
                                                    <div className="home-page-meta-item">
                                                        <span className="home-page-meta-value genres">
                                                            {anime.genres?.slice(0, 3).join(', ') || 'Нет жанров'}
                                                        </span>
                                                    </div>
                                                    <div className="home-page-meta-item">
                                                        <span className="home-page-meta-value">
                                                            {anime.animeStatus === 'ONGOING' ? 'Онгоинг' :
                                                                anime.animeStatus === 'COMPLETED' ? 'Завершен' :
                                                                    anime.animeStatus === 'UPCOMING' ? 'Анонсирован' :
                                                                        anime.animeStatus || 'Не указан'}
                                                        </span>
                                                    </div>
                                                    {anime.sourceMaterial && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-value">
                                                                {anime.sourceMaterial}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {anime.releaseDate && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-value">
                                                                {formatDate(anime.releaseDate)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {anime.season && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-value">
                                                                {getSeasonDisplayName(anime.season)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {anime.ageRating && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-value">
                                                                {formatAgeRating(anime.ageRating)} {/* Использовать функцию форматирования */}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {anime.studios && anime.studios.length > 0 && (
                                                        <div className="home-page-meta-item">
                                                            <span className="home-page-meta-value">
                                                                {anime.studios.slice(0, 2).join(', ')}
                                                                {anime.studios.length > 2 && '...'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Описание снизу на всю ширину */}
                                    <div className="featured-mini-description full-width">
                                        {anime.description && anime.description.length > 0 ? (
                                            <p>{anime.description}</p>
                                        ) : (
                                            <p>Описание отсутствует.</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Индикаторы карусели */}
                        <div className="carousel-indicators">
                            {topThreeSeasonAnime.map((_, index) => (
                                <button
                                    key={index}
                                    className={`indicator ${index === currentFeaturedIndex ? 'active' : ''}`}
                                    onClick={() => setCurrentFeaturedIndex(index)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Блок текущего сезона */}
                    <div className="seasonal-section">
                        <div className="season-card">
                            <div className="season-header">
                                <h4
                                    {...makeClickable(`/anime?season=${currentSeason}&releaseYear=${currentYear}`)}
                                    style={{cursor: 'pointer'}}
                                >
                                    {getSeasonDisplayName(currentSeason)} {currentYear}
                                </h4>
                            </div>

                            <div className="season-anime-list">
                                {currentSeasonAnime.map((anime, index) => (
                                    <div
                                        key={anime.id}
                                        className="season-anime-item"
                                        {...makeClickable(`/anime/${anime.id}`)}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                                handleAnimeClick(anime.id);
                                            }
                                        }}
                                        style={{cursor: 'pointer'}}
                                    >
                                        <img
                                            src={anime.imageUrl}
                                            alt={anime.title}
                                            className="season-anime-image"
                                        />
                                        <div className="season-anime-info">
                                            <span className="season-anime-title">{anime.title}</span>
                                            <span className="season-anime-rating">
                                                <Star className="star-icon" size={11}/>
                                                <span className="season-rating-value">
                                                    {anime.rating && anime.rating > 0 ? anime.rating.toFixed(1) : '0.0'}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {/* Закрывающий тег для .main-content */}

                {/* Anime Section */}
                <div className="anime-horizontal-section">
                    <div className="section-header">
                        <div className="section-title-wrapper">
                            <h2
                                className="anime-subtitle clickable-text"
                                {...makeClickable('/anime')}
                                style={{cursor: 'pointer'}}
                            >
                                Аниме
                            </h2>
                        </div>
                    </div>

                    {randomLoading ? (
                        <div className="loading-horizontal">Loading anime...</div>
                    ) : randomAnime.length > 0 ? (
                        <div className="carousel-container">
                            {/* Кнопка "Назад" */}
                            <button
                                className="carousel-nav-button prev"
                                onClick={handleRandomPrev}
                                disabled={isAtStart()}
                            >
                                ‹
                            </button>

                            {/* Кнопка "Вперед" */}
                            <button
                                className="carousel-nav-button next"
                                onClick={handleRandomNext}
                                disabled={isAtEnd()}
                            >
                                ›
                            </button>

                            <div
                                className="horizontal-scroll-container"
                                ref={randomContainerRef}
                                onScroll={handleRandomScroll}
                            >
                                <div className="horizontal-scroll">
                                    {randomAnime.map((anime) => (
                                        <div
                                            key={anime.id}
                                            className="horizontal-anime-card-grid"
                                            {...makeClickable(`/anime/${anime.id}`)}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                                    handleAnimeClick(anime.id);
                                                }
                                            }}
                                            style={{cursor: 'pointer'}}
                                        >
                                            {/* Картинка */}
                                            <div className="card-image-grid">
                                                <img
                                                    src={anime.imageUrl}
                                                    alt={anime.title}
                                                    className="card-image-grid-item"
                                                    onError={(e) => {
                                                        e.currentTarget.src = "https://images.unsplash.com/photo-1541562232579-512a21360020?w=200&h=280&fit=crop";
                                                    }}
                                                />
                                            </div>

                                            {/* Информация */}
                                            <div className="card-info-grid">
                                                <h3 className="card-title-grid">{anime.title}</h3>
                                                <div className="card-meta-grid">
                                                    <span className="meta-episodes-grid">{anime.episodes} эп.</span>
                                                    <span className="meta-separator">/</span>
                                                    <span className="meta-rating-grid">
                                        <Star className="star-icon" size={11}/>
                                        <span className="home-rating-value">
                                            {anime.rating && anime.rating > 0 ? anime.rating.toFixed(1) : '0.0'}
                                        </span>
                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="empty-state-horizontal">
                            <p>No anime found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


export default HomePage;